import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useGuest, listGuestTokens, addGuestToken, removeGuestToken } from '@/lib/guest';
import { getMe, getGuestTrips, ApiError } from '@/lib/api';
import Icon from './Icon';
import SearchModal from './SearchModal';
import ThemeToggle from './ThemeToggle';
import FeedbackLink from './FeedbackLink';
import type { TripListItem } from '@/lib/types';
import { localDateISO } from '@/lib/dates';

// Top-bar + main outlet. The shell is shared across all non-trip routes (/,
// /new, /reservations) so the app feels of-a-piece. Title links back to the
// trips list. Guest sessions (share-link followers) lose the New-trip
// affordance — their `/` is the read-only shared-trips home.

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);

// The catalog detail + browse routes are trip-scoped, so a search launched
// from the non-trip pages still needs a trip to navigate into. Prefer the
// next-up upcoming/active trip; fall back to the first non-draft, then the
// first trip the user has.
function pickContextTrip(trips: TripListItem[]): TripListItem | undefined {
  const today = localDateISO();
  const dated = trips.filter((t) => t.dates?.lockedStart && t.dates?.lockedEnd);
  const upcoming = dated
    .filter((t) => !t.draft && (t.dates!.lockedEnd ?? '') >= today)
    .sort((a, b) => (a.dates!.lockedStart ?? '').localeCompare(b.dates!.lockedStart ?? ''));
  return upcoming[0] ?? trips.find((t) => !t.draft) ?? trips[0];
}

// Search affordance styled like TripShell's top-bar pill, but it opens the
// (whole-catalog) modal on click rather than binding live to a trip's ?q=.
function SearchPill({ onOpen }: { onOpen: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Search the catalog"
      className="app-bar-search"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        height: 38,
        padding: '0 12px 0 14px',
        width: 'min(440px, 42vw)',
        borderRadius: 'var(--r-pill)',
        border: `1px solid ${hover ? 'var(--accent-soft)' : 'var(--line)'}`,
        background: hover ? 'var(--surface)' : 'var(--surface-2)',
        cursor: 'text',
        fontFamily: 'var(--font-ui)',
        transition: 'background var(--dur-fast), border-color var(--dur-fast)',
      }}
    >
      <Icon name="search" size={15} style={{ color: 'var(--ink-mute)', flexShrink: 0 }} />
      <span
        style={{
          flex: 1,
          textAlign: 'left',
          fontSize: 13,
          color: 'var(--ink-mute)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Search the catalog — restaurants, stays, sights…
      </span>
      <kbd
        style={{
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          color: 'var(--ink-mute)',
          border: '1px solid var(--line)',
          borderRadius: 5,
          padding: '1px 6px',
          flexShrink: 0,
        }}
      >
        {IS_MAC ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
}

export default function AppShell({ children }: { children: ReactNode }) {
  const { isGuest, anonymous } = useGuest();
  const location = useLocation();
  const [contextTrip, setContextTrip] = useState<TripListItem | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Resolve a trip to anchor search. Members read /api/me; guests (share-link
  // followers) can't, so we collect their trips from the stored grant tokens
  // — same source TripsList uses — so search shows on the homepage for them
  // too. Dead tokens are pruned. Guest search is trip-scoped (their grant
  // covers specific trips, not the whole catalog); members get global.
  useEffect(() => {
    let cancelled = false;
    if (isGuest) {
      (async () => {
        const byId = new Map<string, TripListItem>();
        for (const token of listGuestTokens()) {
          try {
            const res = await getGuestTrips(token);
            addGuestToken(
              token,
              res.trips.map((t) => t.tripId),
            );
            for (const t of res.trips) if (!byId.has(t.tripId)) byId.set(t.tripId, t);
          } catch (err) {
            if (err instanceof ApiError && [401, 403, 404].includes(err.status)) {
              removeGuestToken(token);
            }
          }
        }
        if (!cancelled) setContextTrip(pickContextTrip([...byId.values()]) ?? null);
      })();
      return () => {
        cancelled = true;
      };
    }
    getMe()
      .then((res) => {
        if (!cancelled) setContextTrip(pickContextTrip(res.trips) ?? null);
      })
      .catch(() => {
        /* anon / no trips → no shell search; everything else still renders */
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest]);

  // The browser tab names the page (trip pages set their own in TripShell).
  useEffect(() => {
    const titles: Record<string, string> = { '/': 'Trips', '/new': 'New trip', '/reservations': 'Reservations', '/feedback': 'Feedback', '/dining': 'Dining', '/lodging': 'Lodging' };
    const t = titles[location.pathname];
    document.title = t ? `${t} · Tripper Keeper` : 'Tripper Keeper';
  }, [location.pathname]);

  // ⌘K / ^K (and bare `/` outside a field) opens the search modal — same
  // shortcut as the trip-scoped shell, so search is reachable on every page.
  useEffect(() => {
    if (!contextTrip) return;
    const onKey = (e: KeyboardEvent) => {
      const cmdK =
        (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K');
      const t = e.target as HTMLElement | null;
      const inField =
        !!t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable);
      const slash =
        e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && !inField;
      if (cmdK || slash) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contextTrip]);

  return (
    <div className="app-shell">
      <header className="app-bar">
        <h1 className="app-bar-title">
          <Link to="/">Tripper Keeper</Link>
        </h1>
        {contextTrip && <SearchPill onOpen={() => setSearchOpen(true)} />}
        <div className="app-bar-tools" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!isGuest && <FeedbackLink />}
          <ThemeToggle />
        </div>
        {!isGuest ? (
          <Link to="/new" className="btn btn-primary">
            + New trip
          </Link>
        ) : (
          anonymous && (
            <a
              className="btn btn-primary"
              href={`/signin?next=${encodeURIComponent(location.pathname + location.search)}`}
            >
              Sign in
            </a>
          )
        )}
      </header>
      <main className="app-main">{children}</main>
      {searchOpen && contextTrip && (
        <SearchModal
          tripId={contextTrip.tripId}
          global={!isGuest}
          onClose={() => setSearchOpen(false)}
        />
      )}
    </div>
  );
}
